/** @jsxImportSource @emotion/react */
import { useContext, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import type { ReactElement, ReactNode } from 'react';
import { Formik, Form } from 'formik';
import yup from 'utils/yup';
import ContactUsModal from 'components/Brief/ArtifactNavbar/ContactUsModal';
import ValidatedFormikField from 'components/Utils/Forms/ValidatedFormikField';
import useToggle from 'hooks/useToggle';
import { verifyAndLinkWithLocalAccount } from 'api-client/accounts';
import {
  SmartLoginStateContext,
  SmartLoginActionsContext,
  FormStepEnum,
} from './context/SmartLoginContextProvider';
import { ODSAlert, ODSButton, ODSGrid, ODSGroup } from 'bc-ods';

type LinkError =
  | { type: 'rate-limited' }
  | { type: 'locked' }
  | { type: 'already-linked' }
  | { type: 'deactivated' }
  | null;

const messages = defineMessages({
  heading: {
    id: 'SSOLinkAccount.heading',
    defaultMessage: 'Link your account',
  },
  subtitle: {
    id: 'SSOLinkAccount.subtitle',
    defaultMessage:
      "Enter the email and password for the Onebrief account you'd like to link with your SSO identity.",
  },
  localAccountPassword: {
    id: 'SSOLinkAccount.localAccountPassword',
    defaultMessage: 'Password',
  },
  localAccountError: {
    id: 'SSOLinkAccount.localAccountError',
    defaultMessage: "Password doesn't match, try again.",
  },
  buttonBack: {
    id: 'SSOLinkAccount.buttonBack',
    defaultMessage: 'Back',
  },
  link: {
    id: 'SSOLinkAccount.link',
    defaultMessage: 'Link account',
  },
  usernameLabel: {
    id: 'SSOLinkAccount.usernameLabel',
    defaultMessage: 'Email or username',
  },
  usernamePlaceholder: {
    id: 'SSOLinkAccount.usernamePlaceholder',
    defaultMessage: 'e.g. Dave',
  },
  usernameFeedback: {
    id: 'SSOLinkAccount.usernameFeedback',
    defaultMessage:
      '{error, select, min {Must be at least 3 characters long} noSpaces {No spaces allowed} other {Required}}',
  },
  forgotPassword: {
    id: 'SSOLinkAccount.forgotPassword',
    defaultMessage: 'Forgot password',
  },
  help: {
    id: 'SSOLinkAccount.help',
    defaultMessage: 'Help',
  },
  linkingUnavailableHeading: {
    id: 'SSOLinkAccount.linkingUnavailableHeading',
    defaultMessage: 'Linking temporarily unavailable',
  },
  linkingUnavailableBody: {
    id: 'SSOLinkAccount.linkingUnavailableBody',
    defaultMessage:
      'This account was recently linked to another account. Linking is temporarily unavailable for security.',
  },
  linkingUnavailableAlert: {
    id: 'SSOLinkAccount.linkingUnavailableAlert',
    defaultMessage:
      'Try logging in after one day or contact your customer representative for support.',
  },
  accountLockedHeading: {
    id: 'SSOLinkAccount.accountLockedHeading',
    defaultMessage: 'Account temporarily locked',
  },
  accountLockedBody: {
    id: 'SSOLinkAccount.accountLockedBody',
    defaultMessage: 'Your account has been temporarily locked for security.',
  },
  accountLockedAlert: {
    id: 'SSOLinkAccount.accountLockedAlert',
    defaultMessage: 'This account is temporarily locked. Try again in 15 minutes, or reset your password.',
  },
  accountUnavailableHeading: {
    id: 'SSOLinkAccount.accountUnavailableHeading',
    defaultMessage: 'Account unavailable',
  },
  accountUnavailableBody: {
    id: 'SSOLinkAccount.accountUnavailableBody',
    defaultMessage:
      "The account you're trying to link is already connected to a different SSO identity. Only one SSO identity can be linked per account.",
  },
  accountUnavailableAlert: {
    id: 'SSOLinkAccount.accountUnavailableAlert',
    defaultMessage:
      'Try linking to a different account or contact your customer representative to unlink this account and find the correct one.',
  },
  accountDeactivatedHeading: {
    id: 'SSOLinkAccount.accountDeactivatedHeading',
    defaultMessage: 'Account deactivated',
  },
  accountDeactivatedBody: {
    id: 'SSOLinkAccount.accountDeactivatedBody',
    defaultMessage:
      'Your account has been deactivated by a customer representative. You are unable to access Onebrief at this time.',
  },
  accountDeactivatedAlert: {
    id: 'SSOLinkAccount.accountDeactivatedAlert',
    defaultMessage:
      'If you believe this is a mistake, contact your customer representative for support.',
  },
  linkDifferentAccount: {
    id: 'SSOLinkAccount.linkDifferentAccount',
    defaultMessage: 'Link a different account',
  },
  resetPassword: {
    id: 'SSOLinkAccount.resetPassword',
    defaultMessage: 'Reset password',
  },
});

function getLinkErrorDisplay(linkError: NonNullable<LinkError>): {
  heading: ReactNode;
  body: ReactNode;
  alertColor: string;
  alertContent: ReactNode;
} {
  switch (linkError.type) {
    case 'rate-limited':
      return {
        heading: <FormattedMessage {...messages.linkingUnavailableHeading} />,
        body: <FormattedMessage {...messages.linkingUnavailableBody} />,
        alertColor: 'warning',
        alertContent: <FormattedMessage {...messages.linkingUnavailableAlert} />,
      };
    case 'locked':
      return {
        heading: <FormattedMessage {...messages.accountLockedHeading} />,
        body: <FormattedMessage {...messages.accountLockedBody} />,
        alertColor: 'danger',
        alertContent: <FormattedMessage {...messages.accountLockedAlert} />,
      };
    case 'already-linked':
      return {
        heading: <FormattedMessage {...messages.accountUnavailableHeading} />,
        body: <FormattedMessage {...messages.accountUnavailableBody} />,
        alertColor: 'warning',
        alertContent: <FormattedMessage {...messages.accountUnavailableAlert} />,
      };
    case 'deactivated':
      return {
        heading: <FormattedMessage {...messages.accountDeactivatedHeading} />,
        body: <FormattedMessage {...messages.accountDeactivatedBody} />,
        alertColor: 'warning',
        alertContent: <FormattedMessage {...messages.accountDeactivatedAlert} />,
      };
  }
}

const INITIAL_VALUES = { username: '', obPassword: '' };

function SSOLinkAccount(): ReactElement {
  const [showError, setShowError] = useState(false);
  const [linkError, setLinkError] = useState<LinkError>(null);
  const { formError, profile, provider, matchingId } = useContext(SmartLoginStateContext);
  const { setFormError, setFormStep, goToPreviousStep, setDiscoveredAccountDetails } = useContext(SmartLoginActionsContext);
  const [contactUsModalVisible, toggleContactUsModal] = useToggle();

  const intl = useIntl();

  let errorDetails = intl.formatMessage(messages.localAccountError);

  const linkAccountValidationSchema = yup.object().shape({
    username: yup.string().required('required').min(3, 'min').matches(/^\S*$/, 'noSpaces'),
    obPassword: yup.string().required('required').min(3, 'min'),
  });

  const handleVerifyLocalAccount = async (values: any) => {
    try {
      setShowError(false);
      setLinkError(null);

      const result = await verifyAndLinkWithLocalAccount(
        profile?.username ?? matchingId,
        values.username,
        values.obPassword,
        provider
      );

      if (result.status === 200) {
        const data = result.data as any;
        setDiscoveredAccountDetails({
          avatarColor: data.avatarColor,
          rank: data.rank,
          jobTitle: data.jobTitle,
          organization: data.organization,
          parentOrganization: data.parentOrganization,
        });
        setFormStep(FormStepEnum.SSOLinkSuccess);
      } else if (result.status === 429) {
        setLinkError({ type: 'rate-limited' });
      } else {
        setFormError(errorDetails);
        setShowError(true);
      }
    } catch (error: any) {
      const status = error.response?.status;
      if (status === 423) {
        setLinkError({ type: 'locked' });
      } else if (status === 409) {
        setLinkError({ type: 'already-linked' });
      } else if (status === 401 && !error.response?.data?.error) {
        setLinkError({ type: 'deactivated' });
      } else {
        errorDetails = error.response?.data?.error;
        setFormError(errorDetails ?? 'An unexpected error has occurred. Please try again.');
        setShowError(true);
      }
    }
  };

  if (linkError) {
    const { heading, body, alertColor, alertContent } = getLinkErrorDisplay(linkError);

    return (
      <>
        <ODSGroup stacked data-testid="account-linking-dialog">
          <div>
            <h5 data-testid="link-error-heading">{heading}</h5>
            <p className="text-secondary mb-0" data-testid="link-error-body">
              {body}
            </p>
          </div>
          <ODSAlert className="d-flex align-items-center" color={alertColor} isOpen data-testid="link-error-alert">
            {alertContent}
          </ODSAlert>
          {linkError.type === 'already-linked' && (
            <ODSButton
              size="md"
              data-testid="link-different-account"
              onClick={() => setLinkError(null)}
            >
              <FormattedMessage {...messages.linkDifferentAccount} />
            </ODSButton>
          )}
          {linkError.type === 'locked' && (
            <ODSButton
              color="primary"
              size="md"
              data-testid="reset-password"
              onClick={() => setFormStep(FormStepEnum.Forgot)}
            >
              <FormattedMessage {...messages.resetPassword} />
            </ODSButton>
          )}
        </ODSGroup>
        <ContactUsModal isOpen={contactUsModalVisible} toggle={toggleContactUsModal} />
      </>
    );
  }

  return (
    <>
      <Formik
        validationSchema={linkAccountValidationSchema}
        initialValues={{ ...INITIAL_VALUES, username: matchingId ?? '' }}
        onSubmit={handleVerifyLocalAccount}
      >
        {({ isValid, errors }) => (
          <div data-testid="account-linking-dialog">
            <Form>
              <ODSGrid spacing="md" columns={1}>
                <div>
                  <h5>
                    <FormattedMessage {...messages.heading} />
                  </h5>
                  <p className="text-secondary mb-0">
                    <FormattedMessage {...messages.subtitle} />
                  </p>
                </div>
                {showError && (
                  <ODSAlert className="my-2 d-flex align-items-center" color="danger" isOpen={showError}>
                    {formError}
                  </ODSAlert>
                )}
                <ValidatedFormikField
                  name="username"
                  id="username"
                  size="md"
                  label={<FormattedMessage {...messages.usernameLabel} />}
                  placeholder={intl.formatMessage(messages.usernamePlaceholder)}
                  autoFocus
                  data-testid="username"
                  autoComplete="username"
                  tabIndex={1000}
                  errorMessage={
                    <FormattedMessage
                      {...messages.usernameFeedback}
                      values={{ error: errors.username }}
                    />
                  }
                />
                <div className="gap-0">
                  <ValidatedFormikField
                    name="obPassword"
                    id="obPassword"
                    size="md"
                    label={
                      <>
                        <span className="d-flex justify-content-end gap-2 align-items-center">
                          <span className="me-auto">
                            <FormattedMessage {...messages.localAccountPassword} />
                          </span>
                          <ODSButton
                            quiet
                            type="button"
                            data-testid="button-forgot-password"
                            css={(theme) => ({
                              color: theme.colors.primary,
                              textDecoration: 'underline',
                              fontSize: '0.75rem',
                              padding: 0,
                            })}
                            onClick={() => {
                              setShowError(false);
                              setFormStep(FormStepEnum.Forgot);
                            }}
                          >
                            <FormattedMessage {...messages.forgotPassword} />
                          </ODSButton>
                          <span className="text-secondary" aria-hidden>
                            |
                          </span>
                          <ODSButton
                            quiet
                            type="button"
                            data-testid="button-help"
                            css={(theme) => ({
                              color: theme.colors.primary,
                              textDecoration: 'underline',
                              fontSize: '0.75rem',
                              padding: 0,
                            })}
                            onClick={toggleContactUsModal}
                          >
                            <FormattedMessage {...messages.help} />
                          </ODSButton>
                        </span>
                      </>
                    }
                    autoFocus
                    data-testid="obPassword"
                    type="password"
                  />
                </div>
                <div className="d-flex gap-2 mt-4">
                  <ODSButton
                    className="flex-shrink-0"
                    size="md"
                    quiet
                    onClick={() => {
                      goToPreviousStep();
                    }}
                    data-testid="go-back"
                  >
                    <FormattedMessage {...messages.buttonBack} />
                  </ODSButton>
                  <ODSButton
                    color="primary"
                    size="md"
                    className="flex-grow-1"
                    disabled={!isValid}
                    type="submit"
                    data-testid="submit"
                  >
                    <FormattedMessage {...messages.link} />
                  </ODSButton>
                </div>
              </ODSGrid>
            </Form>
          </div>
        )}
      </Formik>
      <ContactUsModal isOpen={contactUsModalVisible} toggle={toggleContactUsModal} />
    </>
  );
}

export default SSOLinkAccount;
